import { useEffect, useState, useRef } from 'react';
import request from '../../utils/request';
import Select from '../../components/Select/Select';
import './CreateEmployeeModal.css';

function isInsideModalContent(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('.modal-content') !== null;
}

interface CreateEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreateEmployeeResponse {
  id: number;
}

interface RoleItem {
  id: number;
  name: string;
  status: number;
}

interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

interface RoleListData {
  items: RoleItem[];
  total: number;
}

export default function CreateEmployeeModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateEmployeeModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role_id: 0,
    status: 1,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideContent = useRef(false);

  // Load role list
  useEffect(() => {
    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const result = (await request.get('/gradmin/roles')) as ApiResponse<RoleListData>;
      console.log('Role list data:', result);
      // Only show enabled roles
      const enabledRoles = result.data.items.filter((role: RoleItem) => Number(role.status) === 1);
      console.log('Enabled roles:', enabledRoles);
      setRoles(enabledRoles);
    } catch (err) {
      console.error('Failed to fetch role list:', err);
    } finally {
      setRolesLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate role selection
    if (formData.role_id === 0) {
      setError('Please select a role');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await request.post<CreateEmployeeResponse>('/gradmin/employees', formData);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        password: '',
        role_id: 0,
        status: 1,
      });
      setError('');
      onClose();
    }
  };

  // Generate random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={e => {
        mouseDownInsideContent.current = isInsideModalContent(e.target);
      }}
      onClick={e => {
        if (e.target === overlayRef.current && !mouseDownInsideContent.current) {
          handleClose();
        }
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Add Employee</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error-alert">{error}</div>}

            <div className="modal-form-group">
              <label className="modal-form-label">
                Name <span className="modal-required">*</span>
              </label>
              <input
                type="text"
                className="modal-form-input"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter employee name"
                required
                disabled={loading}
              />
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label">
                Email <span className="modal-required">*</span>
              </label>
              <input
                type="email"
                className="modal-form-input"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                required
                disabled={loading}
              />
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label">
                Password <span className="modal-required">*</span>
              </label>
              <div className="modal-password-input-group">
                <div className="modal-password-field-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="modal-form-input"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter password (at least 6 characters)"
                    minLength={6}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="modal-btn-toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                        <line x1="2" x2="22" y1="2" y2="22" />
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                <button
                  type="button"
                  className="modal-btn-generate-password"
                  onClick={generatePassword}
                  disabled={loading}
                >
                  Generate Password
                </button>
              </div>
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label">
                Role <span className="modal-required">*</span>
              </label>
              <Select
                value={formData.role_id || undefined}
                options={roles.map(role => ({ value: role.id, label: role.name }))}
                placeholder="Select a role"
                onChange={value => setFormData({ ...formData, role_id: Number(value) })}
                disabled={loading || rolesLoading}
              />
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label">Status</label>
              <div className="modal-radio-group">
                <label className="modal-radio-label">
                  <input
                    type="radio"
                    name="status"
                    value={1}
                    checked={formData.status === 1}
                    onChange={e => setFormData({ ...formData, status: Number(e.target.value) })}
                    disabled={loading}
                  />
                  <span>Active</span>
                </label>
                <label className="modal-radio-label">
                  <input
                    type="radio"
                    name="status"
                    value={2}
                    checked={formData.status === 2}
                    onChange={e => setFormData({ ...formData, status: Number(e.target.value) })}
                    disabled={loading}
                  />
                  <span>Disabled</span>
                </label>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="modal-btn modal-btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="modal-btn modal-btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
